import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ModalController, ToastController } from '@ionic/angular';
import * as moment from 'moment/moment';
import { ViewGoalsPage } from 'src/app/dashboard/goals-progress/view-goals/view-goals.page';
import { FinalReport } from 'src/app/final-report/_interfaces/final-report.interface';
import { BaseClass } from 'src/app/shared/classes/base';
import { GlobalizationConfig } from 'src/app/shared/interfaces/globalization-config.interface';
import { GoalProgressPreview } from 'src/app/shared/interfaces/goal-progress.interface';
import {
    ProcessConfig,
    QuestionConfig,
    QuestionConfigTypeEnum,
    QuestionGroup, QuestionTypeEnum, ValuesGlobalization
} from 'src/app/shared/interfaces/process-config.interface';
import { ProcessTypeEnum } from 'src/app/shared/interfaces/process.interface';
import { TranslationService } from 'src/app/shared/services/translation.service';
import { environment } from 'src/environments/environment';
import { QuestionAttributePipe } from 'src/app/shared/pipes/question-attribute.pipe';

@Component({
	selector: 'app-self-manager-evaluation-report',
	templateUrl: './self-manager-evaluation.component.html',
	styleUrls: ['./self-manager-evaluation.component.scss']
})
export class SelfManagerEvaluationReportComponent extends BaseClass implements OnChanges {

	@Input() report: FinalReport;
	@Input() readonly showMoreLess: Boolean;
    
    ngOnChanges(changes: SimpleChanges) {
        if (this.showFinalResult && changes.report && changes.report.currentValue) {
            this.getFinalGradeQuestion();
        }
    }

    public ProcessType = ProcessTypeEnum;
	public currentLanguage: string;
    public selfFinalGrade: string = "--";
    public managerFinalGrade: string = "--";
    public showFinalResult = environment.features.showFinalResult;

	public highlightGoalsFinalGradeAnswer = environment.features.highlightSelfOrManagerEvalAnswer;

	constructor(
		protected _toastController: ToastController,
		protected _translationService: TranslationService,
		private _modalController: ModalController,
        private _questionAttributePipe: QuestionAttributePipe
	) {
		super(_toastController, _translationService);
		this.currentLanguage = _translationService.getSelectedLanguage();
	}

	public getText(globalizationConfig: GlobalizationConfig, property: string): string {
		const regexToRemoveTag = /<(?:[A-Za-z_:][\w:.-]*(?=\s)(?!(?:[^>"\']|"[^"]*"|\'[^\']*\')*?(?<=\s)(?:term|range)\s*=)(?!\s*\/?>)\s+(?:".*?"|\'.*?\'|[^>]*?)+|\/?[A-Za-z_:][\w:.-]*\s*\/?)>/g;
		return super.getText(globalizationConfig, property).replace(regexToRemoveTag, '');
	}
	
	public getTextGlobalized(globalizationConfig: ValuesGlobalization, index: number): string {
		const regexToRemoveTag = /<(?:[A-Za-z_:][\w:.-]*(?=\s)(?!(?:[^>"\']|"[^"]*"|\'[^\']*\')*?(?<=\s)(?:term|range)\s*=)(?!\s*\/?>)\s+(?:".*?"|\'.*?\'|[^>]*?)+|\/?[A-Za-z_:][\w:.-]*\s*\/?)>/g;
		return super.getTextGlobalized(globalizationConfig[this.selectedLanguage], index).replace(regexToRemoveTag, '');
	}

	public getQuestionsGroups(processConfigs: Array<ProcessConfig>): Array<QuestionGroup> {
		const config = processConfigs.find(c => c.type === ProcessTypeEnum.SelfEvaluation);

		if (!config || !config.questionsGroups)
			return [];

		config.questionsGroups.sort((a, b) => a.order - b.order);
		let currentIndex = 1;
        config.questionsGroups = config.questionsGroups.filter(g => {
            if (!g.attributes || g.attributes.length == 0)
                return true;
            return g.attributes.some(attr => this.report.user.attributes.some(userAttr => userAttr.id === attr.attributeId));
        })
		config.questionsGroups.forEach(g => {
            g.questions = this._questionAttributePipe.transform(g.questions, this.report.user.attributes);
			g.questions.sort((a, b) => a.order - b.order);
			g.questions.forEach(q => {
				q.currentIndex = currentIndex;
				currentIndex++;
			});
		});

		return config.questionsGroups;
	}

	public shouldHighlight(question: QuestionConfig): boolean {
		return question.type === QuestionConfigTypeEnum.GoalsFinalGrade ||
			question.type === QuestionConfigTypeEnum.FinalGrade ||
			question.type === QuestionConfigTypeEnum.PrinciplesFinalGrade ||
			question.type === QuestionConfigTypeEnum.LeadershipFinalGrade;
	}
    
    public getFinalGradeQuestion(): void {
        
        const selfProcess = this.report.processConfigs.find(x => x.type === ProcessTypeEnum.SelfEvaluation);
        const selfTargetValues = JSON.parse(selfProcess.evaluationValuesTarget);
        this.selfFinalGrade = this.FindFinalGrade(selfProcess, this.report.selfEvaluation, selfTargetValues);
        
        const calibrationXValue = this.report.calibration.quadrantXAxisValue;
        
        if (calibrationXValue != null) this.managerFinalGrade = this.getFinalGradeValue(calibrationXValue, selfTargetValues);
        else
        {
            const manageProcess = this.report.processConfigs.find(x => x.type === ProcessTypeEnum.ManagerEvaluation);
            this.managerFinalGrade = this.FindFinalGrade(manageProcess, this.report.managerEvaluation, selfTargetValues);
        }
    }
    
    public FindFinalGrade(process: ProcessConfig, evaluation: any, targetValues: any): string {
        for(let questionGroup of process.questionsGroups) {
            for(let question of questionGroup.questions) {
                if(question.typeConfig.type === QuestionTypeEnum.Calculate) {
                    return this.getFinalGradeValue(evaluation[`field${question.order}`], targetValues);
                }
            }
        }
    }
    
    public getFinalGradeValue(finalGrade: number , targetValues: any): string {
        for(const key in targetValues) {
            if(finalGrade >= targetValues[key].min && finalGrade < targetValues[key].max) {
                return key;
            }
        }
    }

	public getGoalsQuestion(): QuestionConfig {
		const process = this.report.processConfigs.find(x => x.type === ProcessTypeEnum.SelfEvaluation);

		for (const g of process.questionsGroups) {
			const q = g.questions.find(x => x.type === QuestionConfigTypeEnum.Goals);
			if (q) return q;
		}
		return null;
	}

	public getProgressBarColor(goal: GoalProgressPreview, color: string): boolean {
		const momentDueDate = moment(goal.deadline).startOf('day');
		const momentToday = moment().startOf('day');
		const diff = moment.duration(momentDueDate.diff(momentToday)).asDays();

		if (diff <= 10 && diff > 3)
			return color === 'warning';

		if (diff > 0 && diff <= 3)
			return color === 'danger';

		return color === 'primary';
	}

	public getProgressBarLength(goal: GoalProgressPreview) {
		return { width: goal.currentProgress + '%' };
	}

	public async viewGoalModal(goal: GoalProgressPreview) {
		const goalModal = await this._modalController.create({
			component: ViewGoalsPage,
			cssClass: 'view-goals-modal-style',
			componentProps: {
				'goalId': goal.id,
				'allowComments': false
			}
		});

		goalModal.present();
	}

	getGlobalizedValue(value, question) {
		if (question && question.typeConfig && question.typeConfig.possibleValuesGlobalized && question.typeConfig.possibleValues) {
			const idx = question.typeConfig.possibleValues.findIndex(x => x === value);
			if (idx === -1) return value;

			return question.typeConfig.possibleValuesGlobalized[this.currentLanguage][idx];

		} else
			return value;
	}

}
